import { createPortalNavigationConfig, type PortalNavigationConfig } from "@portal/react-patterns";
import { normalizeRoute } from "@portal/utilities/normalizer";
import { NavLink } from "react-router";
import { root } from "../utilities/frontEndConfig.js";

/**
 * Zeus navigation configuration — exported as a named constant.
 *
 * This module provides a static `navigation` value (canonical `sections`) to
 * be consumed by the application shell. The SideBar component in the shared
 * react-patterns library owns session/auth rendering, so this file only
 * supplies the application-specific sections.
 */

export const navigation: PortalNavigationConfig = createPortalNavigationConfig({
  sections: [
    {
      id: `main`,
      items: [
        {
          id: `home`,
          component: NavLink,
          label: `Home`,
          icon: `drag`,
          to: normalizeRoute(root),
          authorizationChecks: [],
        },
        {
          id: `console`,
          component: NavLink,
          label: `Console`,
          icon: `open-terminal`,
          to: normalizeRoute(`${root}/console`),
          reloadDocument: true,
          authorizationChecks: [
            {
              object: `portal_organization:canonical`,
              relation: `assignee`,
              user: `user:{{sessionEmail}}`,
              context: {
                email: `{{sessionEmail}}`,
              },
            },
          ],
        },
        {
          id: `commercial`,
          component: NavLink,
          label: `Commercial`,
          icon: `topic`,
          to: normalizeRoute(`${root}/commercial`),
          reloadDocument: true,
          authorizationChecks: [
            {
              object: `portal_organization:nonCanonical`,
              relation: `assignee`,
              user: `user:{{sessionEmail}}`,
              context: {
                email: `{{sessionEmail}}`,
              },
            },
          ],
        },
        {
          id: `communication`,
          component: NavLink,
          label: `Communication`,
          icon: `contact`,
          to: normalizeRoute(`${root}/communication`),
          reloadDocument: true,
          authorizationChecks: [
            {
              object: `portal_organization:canonical`,
              relation: `assignee`,
              user: `user:{{sessionEmail}}`,
              context: {
                email: `{{sessionEmail}}`,
              },
            },
          ],
        },
        {
          id: `vendor`,
          component: NavLink,
          label: `Vendor Portal`,
          icon: `models`,
          to: normalizeRoute(`${root}/vendor`),
          reloadDocument: true,
          authorizationChecks: [
            {
              object: `vendor_group:canAccess`,
              relation: `member`,
              user: `user:{{sessionEmail}}`,
              context: {
                email: `{{sessionEmail}}`,
              },
            },
          ],
        },
        {
          id: `partners`,
          component: NavLink,
          label: `Partners`,
          icon: `user-group`,
          to: normalizeRoute(`${root}/partner`),
          authorizationChecks: [{ object: `role:portal/operator`, relation: `assignee`, user: `user:{{sessionEmail}}` }],
        },
        {
          id: `operate`,
          component: NavLink,
          label: `Operate`,
          icon: `settings`,
          to: normalizeRoute(`${root}/operate/operator-list`),
          authorizationChecks: [{ object: `role:portal/operator`, relation: `assignee`, user: `user:{{sessionEmail}}` }],
        },
        {
          id: `storage`,
          component: NavLink,
          label: `Storage`,
          icon: `machines`,
          to: normalizeRoute(`${root}/storage`),
          reloadDocument: true,
          authorizationChecks: [{ object: `role:portal/operator`, relation: `assignee`, user: `user:{{sessionEmail}}` }],
        },
        {
          id: `scheduler`,
          component: NavLink,
          label: `Scheduler`,
          icon: `units`,
          to: normalizeRoute(`${root}/scheduler`),
          reloadDocument: true,
          authorizationChecks: [{ object: `scheduler:chronos`, relation: `can_access`, user: `user:{{sessionEmail}}` }],
        },
        {
          id: `support`,
          component: NavLink,
          label: `Support`,
          icon: `status`,
          to: normalizeRoute(`${root}/support/`),
          reloadDocument: true,
          authorizationChecks: [
            {
              object: `support:knowledgeBase`,
              relation: `can_access`,
              user: `user:{{sessionEmail}}`,
              context: {
                currentTime: `{{currentTime}}`,
              },
            },
          ],
        },
        {
          id: `managed-solutions`,
          component: NavLink,
          label: `Managed Solutions`,
          icon: `status`,
          to: normalizeRoute(`${root}/support/staff/planned-coverage-req`),
          reloadDocument: true,
          authorizationChecks: [
            {
              object: `support:managedSolutions`,
              relation: `can_access`,
              user: `user:{{sessionEmail}}`,
              context: {
                currentTime: `{{currentTime}}`,
              },
            },
          ],
        },
      ],
    },
  ],
});
